import { EXPERIENCES } from '@pass-frontend/data-access/types';
import { env } from '@pass-frontend/features/configuration/util';

const {
  AUTHENTICATED_BASE_PATH,
  FF_ENABLE_YOUR_PERKS,
  FF_RENEWACTIVE_COPY_UPDATES_2026,
  PRODUCT_NAME,
  BASE_PATH,
} = env();

const basePath = BASE_PATH ?? '';
const isRA = PRODUCT_NAME === EXPERIENCES.RA;
const PATH_PREFIX = `${isRA ? '' : basePath}${AUTHENTICATED_BASE_PATH}`;

// OnePass Application Paths
export const EVENTS_PATH = `${PATH_PREFIX}/social/events`;
export const FITBIT_PATH = `${PATH_PREFIX}/social/fitbit`;

export const GYMS_PATH = `${PATH_PREFIX}/fitness/gyms`;
export const ONLINE_FITNESS_PATH = `${PATH_PREFIX}/fitness/online`;

export const STANDALONE_PATHS = {
  ZIP_CODE_CHECK: `${PATH_PREFIX}/zip-code-check`,
  ELIGIBILITY_CHECK: `${basePath}/eligibility-check`,
  DASHBOARD: `${PATH_PREFIX}/`,
  MEMBER_CODE_CHECK: `${basePath}/member-code-check`,
  FAQS: `${basePath}/faqs`,
  PRIVACY_POLICY: `${basePath}/privacy-policy`,
  HEALTH_DATA_PRIVACY_POLICY: `${basePath}/health-data-privacy-policy`,
  TERMS_OF_USE: `${basePath}/terms-of-use`,
  ACTIVATE_MEMBERSHIP: `${basePath}/activate-membership`,
} as const;

export type StandalonePath =
  (typeof STANDALONE_PATHS)[keyof typeof STANDALONE_PATHS];

export const FACILITY_PATHS = {
  DASHBOARD: `${PATH_PREFIX}/`,
  SIGN_IN: `${PATH_PREFIX}/sign-in`,
  LOCATION_SEARCH_RESULTS: `${PATH_PREFIX}/location-search-results`,
  LOCATION_ACCESS: `${PATH_PREFIX}/location-access`,
} as const;

export type FacilityPath = (typeof FACILITY_PATHS)[keyof typeof FACILITY_PATHS];

export const UNAUTHORIZED_PATH = `${PATH_PREFIX}/unauthorized`;

export const UNAUTHENTICATED_UNAUTHORIZED_PATH = `${basePath}/unauthorized`;

export const AUTHENTICATION_PATH = `${PATH_PREFIX}/auth`;

export const REGISTER_WITH_HSID_PATH = `${PATH_PREFIX}/register-with-hsid`; // unused

export const ONBOARDING_PLAN_PATH = `${PATH_PREFIX}/onboard/plan`;

export const ONBOARDING_PLAN_PICK_PATH = `${ONBOARDING_PLAN_PATH}/pick`;

export const ONLINE_FITNESS_GYMS = `${PATH_PREFIX}/onboard/fitness/online`; // unused

export const MEMBERSHIP_PATH = `${PATH_PREFIX}/membership`;

export const MEMBER_INFO_PATH = `${PATH_PREFIX}/member-info`;

export const GOVERNMENT_MEMBERSHIP_PATH = `${PATH_PREFIX}/membership/government`;

export const HOME_DELIVERY_PATH = `${PATH_PREFIX}/delivery`;

export const BRAIN_HEALTH_PATH = `${PATH_PREFIX}/brain-health`;

export const SOCIAL_FITBIT_PATH = `${PATH_PREFIX}/social/fitbit`;

export const MOBILE_APP_PATH = `${PATH_PREFIX}/mobile-app`;

export const DASHBOARD_PATH = PATH_PREFIX === '' ? '/' : `${PATH_PREFIX}`;

export const RENEW_TNC = `/renew-active-terms-and-conditions`;

export const RENEW_FAQ = `/renew-active-faqs`;

export const QUALIFIED_MEMBER_LOGIN_PATH = `${PATH_PREFIX}/qualified-member-login`;

export const INVOICES_PATH = `${PATH_PREFIX}/invoices`;

export const ADD_GOVERNMENT_DEPENDENT_PATH = `${PATH_PREFIX}/gov-dependents/add`;

export const ADD_DEPENDENT_PATH = `${PATH_PREFIX}/dependents/add`;

export const ADD_DEPENDENT_SUCCESS_PATH = `${PATH_PREFIX}/dependents/confirm/add/success`;

export const getRemoveGovernmentDependentPath = (memberId: string) => {
  return `${PATH_PREFIX}/gov-dependents/${memberId}/remove`;
};

export const REMOVE_GOVERNMENT_DEPENDENT_SUCCESS = `${PATH_PREFIX}/gov-dependents/confirm/remove/success`;

export const ADD_GOVERNMENT_DEPENDENT_SUCCESS = `${PATH_PREFIX}/gov-dependents/confirm/add/success`;

export const PURCHASE_PATH = `${PATH_PREFIX}/purchase`;
export const REVIEW_PATH = `${PATH_PREFIX}/review`;
export const EDIT_PAYMENT_PATH = `${PATH_PREFIX}/edit-payment`;
export const PLAN_CHANGE_PATH = `${PATH_PREFIX}/plan/change`;
export const PLAN_CANCEL_PATH = `${PATH_PREFIX}/plan/cancel`;

export const MEMBER_STEP_UP_PATH = `${PATH_PREFIX}/preview/complete-registration`;

export interface pathProps {
  key: string;
  url: string;
  name: string;
  triggerElementName?: string;
}

export const mappedPaths: pathProps[] = [
  {
    key: `DASHBOARD`,
    url: `${DASHBOARD_PATH}` || `/one-pass`,
    name: ``,
    triggerElementName: `dashboard`,
  },
  {
    key: `SOCIAL_EVENTS`,
    url: `${EVENTS_PATH}`,
    name:
      isRA && FF_RENEWACTIVE_COPY_UPDATES_2026
        ? `Stay Connected`
        : `Social Events`,
    triggerElementName: `get-social`,
  },
  {
    key: `SOCIAL_FITBIT`,
    url: `${FITBIT_PATH}`,
    name: `Fitbit Community`,
  },
  {
    key: `FITNESS_GYMS`,
    url: `${GYMS_PATH}`,
    name: `Find Gyms`,
    triggerElementName: `fitness`,
  },
  {
    key: `FITNESS_ONLINE`,
    url: `${ONLINE_FITNESS_PATH}`,
    name: `Online Fitness`,
  },
  {
    key: `DELIVERY`,
    url: `${HOME_DELIVERY_PATH}`,
    name: FF_ENABLE_YOUR_PERKS ? `Your Perks` : `Home Delivery`,
  },
  {
    key: `BRAIN_HEALTH`,
    url: `${BRAIN_HEALTH_PATH}`,
    name:
      isRA && FF_RENEWACTIVE_COPY_UPDATES_2026
        ? `Stay Focused`
        : `Brain Health`,
    triggerElementName: `brain-health`,
  },
  {
    key: `ONBOARD`,
    url: `${ONBOARDING_PLAN_PICK_PATH}`,
    name: `Choose Membership Tier`,
  },
  {
    key: `MEMBERSHIP_PURCHASE`,
    url: `${PURCHASE_PATH}`,
    name: `Purchase Membership`,
  },
  {
    key: `MEMBERSHIP_MANAGE`,
    url: `${MEMBERSHIP_PATH}`,
    name: `Manage Plan`,
  },
  {
    key: `MEMBERSHIP_CHANGE`,
    url: `${PLAN_CHANGE_PATH}`,
    name: `Change Membership`,
  },
  {
    key: `MEMBERSHIP_CANCEL`,
    url: `${PLAN_CANCEL_PATH}`,
    name: `Cancel membership`,
  },
  {
    key: `PAYMENT_REVIEW`,
    url: `${REVIEW_PATH}`,
    name: `Review Payment`,
  },
  {
    key: `PAYMENT_EDIT`,
    url: `${EDIT_PAYMENT_PATH}`,
    name: `Edit Payment Information`,
  },
  {
    key: `UNAUTHORIZED`,
    url: `${UNAUTHORIZED_PATH}`,
    name: `Unauthorized`,
  },
  {
    key: `TERMS_AND_CONDITIONS`,
    url: `${RENEW_TNC}`,
    name: `Terms and Conditions`,
  },
  {
    key: `FAQ`,
    url: `${RENEW_FAQ}`,
    name: `Frequently Asked Questions`,
  },
];
